import type { TFunction } from 'i18next'
import type { NotificationDetail } from './api'
import { formatMessageId, formatMinutes, formatSignedMinutes } from './format'

export interface Field {
    label: string
    value: string
    tone?: 'good' | 'bad'
}

export const str = (v: unknown): string | null => (v == null || v === '' ? null : String(v))
export const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : typeof v === 'string' && v !== '' && Number.isFinite(Number(v)) ? Number(v) : null)

/** The expanded row's fields per kind, from `details` (docs/ticker-design.md §5.1). Absent facts are left out. */
export function buildFields(d: NotificationDetail, t: TFunction, lang: string, when: (v: unknown) => string | null): Field[] {
    const x = d.details ?? {}
    const out: Field[] = []
    const add = (labelKey: string, value: string | null | undefined, tone?: Field['tone']) => {
        if (value != null && value !== '') out.push({ label: t(`ticker.fields.${labelKey}`), value, tone })
    }
    const minutes = (v: unknown) => {
        const n = num(v)
        return n == null ? null : formatMinutes(n, lang)
    }
    const object = (id: unknown, description: unknown) => {
        const a = str(id)
        const b = str(description)
        return a && b ? `${a} · ${b}` : a ?? b
    }
    const task = (no: unknown, total: unknown, name: unknown) => {
        const n = num(no)
        const tot = num(total)
        const nm = str(name)
        const head = n != null && tot != null ? t('ticker.values.taskOf', { n, total: tot }) : n != null ? String(n) : null
        return head && nm ? `${head} · ${nm}` : head ?? nm
    }

    add('messageId', formatMessageId(d.id))
    add('recoverySet', d.set_description ?? str(x.setDescription))

    switch (d.kind) {
        case 'task_complete': {
            add('object', object(x.objectId ?? d.object_id, x.objectDescription))
            add('runbook', str(x.runbookName))
            add('task', task(x.taskNo, x.totalTasks, x.taskName))
            add('engineer', str(x.authorName) ?? d.author?.name)
            add('started', when(x.startedAt))
            add('completed', when(x.completedAt))
            add('duration', minutes(x.durationMinutes))
            const pct = num(x.objectProgressPct)
            add('objectProgress', pct == null ? null : `${Math.round(pct)}%`)
            break
        }
        case 'object_complete': {
            add('object', object(x.objectId ?? d.object_id, x.objectDescription))
            const total = num(x.totalTasks)
            add('tasks', total == null ? null : t('ticker.values.tasksComplete', { total }))
            add('engineer', str(x.authorName) ?? d.author?.name)
            add('started', when(x.startedAt))
            add('completed', when(x.completedAt))
            add('rtaTarget', when(x.rtaTarget))
            // The server sends a code: 'on_plan' (finished by its original RTA) or 'late'.
            const status = str(x.rtaStatus)
            if (status === 'on_plan') add('rtaStatus', t('ticker.values.rtaStatusMet'), 'good')
            else if (status === 'late') add('rtaStatus', t('ticker.values.rtaStatusLate'), 'bad')
            else add('rtaStatus', status, status ? rtaStatusTone(status) : undefined)
            break
        }
        case 'task_issue': {
            add('object', object(x.objectId ?? d.object_id, x.objectDescription))
            add('task', task(x.taskNo, x.totalTasks, x.taskName))
            const level = str(x.level)
            add('issueLevel', level === 'task' ? t('ticker.values.levelTask') : level === 'runbook' ? t('ticker.values.levelRunbook') : level)
            add('engineer', str(x.authorName) ?? d.author?.name)
            add('raised', when(x.raisedAt))
            add('openFor', minutes(x.openForMinutes), 'bad')
            const esc = num(x.escalation)
            add('escalation', esc == null ? null : `#${esc}`)
            const blocking = Array.isArray(x.blocking)
                ? (x.blocking as { taskNo?: unknown; taskName?: unknown }[])
                      .map((b) => t('ticker.values.taskRef', { n: str(b?.taskNo) ?? '?', name: str(b?.taskName) ?? '' }).trim())
                      .join(', ')
                : null
            add('blocking', blocking, 'bad')
            break
        }
        case 'engineer_update': {
            add('object', object(x.objectId ?? d.object_id, x.objectDescription))
            add('action', actionLabel(str(x.action), t))
            add('relatedTask', str(x.relatedTaskNo))
            add('author', str(x.authorName) ?? d.author?.name)
            add('posted', when(d.created_at))
            break
        }
        case 'management_update': {
            add('author', str(x.authorName) ?? d.author?.name)
            add('posted', when(d.created_at))
            break
        }
        case 'rta_change': {
            add('mainObject', object(x.mainObjectId, x.mainObjectDescription))
            add('originalRta', when(x.originalRta))
            add('forecastFinish', when(x.forecastFinish))
            const direction = str(x.direction)
            const tone = direction === 'slip' ? 'bad' : direction === 'gain' || direction === 'on_plan' ? 'good' : undefined
            const dev = num(x.deviationMinutes)
            add('deviation', dev == null ? null : formatSignedMinutes(dev, lang), tone)
            const change = num(x.changeSinceLastMinutes)
            add('changeSinceLast', change == null ? null : formatSignedMinutes(change, lang))
            add(
                'direction',
                direction === 'slip'
                    ? t('ticker.values.directionSlip')
                    : direction === 'gain'
                      ? t('ticker.values.directionGain')
                      : direction === 'on_plan'
                        ? t('ticker.values.directionOnPlan')
                        : direction,
                tone,
            )
            const alertNo = num(x.alertNumber)
            add('alertNumber', alertNo == null ? null : `#${alertNo}`)
            add('sincePrevious', minutes(x.minutesSincePrevious))
            const pct = num(x.percentComplete)
            add('percentComplete', pct == null ? null : `${Math.round(pct)}%`)
            add('remaining', minutes(x.remainingMinutes))
            break
        }
    }
    return out
}

/**
 * An engineer update's `action` is the RecoveryAssignments action it was posted with, one word of
 * the runbook log's vocabulary (backend/src/lib/recovery-runbook-status.js, plus Issue). The server
 * compares them ignoring case, and older rows write "In Progress" with a space. Anything else is
 * shown as sent.
 */
const ACTION_KEYS: Record<string, string> = {
    assigned: 'actionAssigned',
    unassigned: 'actionUnassigned',
    started: 'actionStarted',
    inprogress: 'actionInProgress',
    completed: 'actionCompleted',
    issue: 'actionIssue',
    reopened: 'actionReopened',
    bypassed: 'actionBypassed',
    bypassreverted: 'actionBypassReverted',
}

export function actionLabel(action: string | null, t: TFunction): string | null {
    if (action == null) return null
    const key = ACTION_KEYS[action.replace(/\s+/g, '').toLowerCase()]
    return key ? t(`ticker.values.${key}`) : action
}

/** rtaStatus is free text from the server ("Met — 1h 12m ahead"); colour it only when it says which way. */
function rtaStatusTone(status: string): Field['tone'] {
    if (/\b(miss|missed|late|behind|breach|breached|overdue)\b/i.test(status)) return 'bad'
    if (/\b(met|ahead|on track|on time)\b/i.test(status)) return 'good'
    return undefined
}
